// components/common/Layout.tsx
import { useEffect, useRef, useState } from 'react';
import { useAuth } from "../../context/Auth/auth-context";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Navbar } from './Navbar';

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, auth, userInfo, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Admin-only avatar menu. Bot Lab lives here — behind a deliberate click — so a
  // shared/screen-captured window never shows the word "bots" anywhere.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [menuOpen]);

  // Close the menu on any navigation so it never lingers into a shared screen.
  useEffect(() => setMenuOpen(false), [pathname]);

  // The presenter route IS the broadcast — it gets captured into YouTube. No
  // navbar, no sign-out button, no brand chrome on stream: just the stage.
  if (pathname.endsWith('/present')) {
    return <>{children}</>;
  }

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const photo = userInfo?.photoURL || auth?.currentUser?.photoURL;
  const name = userInfo?.name || auth?.currentUser?.displayName || userInfo?.email;

  return (
    <div className="min-h-screen bg-paper text-slate-900">
      <header className="sticky top-0 z-20 bg-paper/95 border-b-2 border-ink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center gap-2 font-extrabold text-xl tracking-tight">
                <span className="grid grid-cols-2 w-8 h-8 rounded-lg overflow-hidden border-2 border-ink shrink-0">
                  <span className="bg-tile-red" /><span className="bg-tile-blue" />
                  <span className="bg-tile-gold" /><span className="bg-tile-green" />
                </span>
                <span className="flex flex-col leading-none">
                  <span className="font-display">Quiz<span className="text-tile-red">Live</span></span>
                  <span className="text-[10px] font-medium tracking-normal text-ink/40 mt-0.5">by aliensbrain</span>
                </span>
              </Link>
              <Navbar />
            </div>
            {user && (
              <div className="flex items-center gap-3">
                {(userInfo?.role === 'admin' || userInfo?.role === 'presenter') && (
                  <span className="hidden sm:inline chip bg-ink text-accent">
                    {userInfo.role === 'admin' ? 'ADMIN' : 'HOST'}
                  </span>
                )}
                {userInfo?.role === 'admin' ? (
                  // Deliberate-access menu: closed it's just the avatar + a chevron,
                  // so nothing bot-related can appear on a shared screen.
                  <div ref={menuRef} className="relative">
                    <button
                      onClick={() => setMenuOpen((o) => !o)}
                      className="flex items-center gap-1"
                      aria-haspopup="menu"
                      aria-expanded={menuOpen}
                      aria-label="Host menu"
                    >
                      {photo ? (
                        <img src={photo} alt="" className="w-8 h-8 rounded-full ring-2 ring-slate-200" />
                      ) : (
                        <span className="grid place-items-center w-8 h-8 rounded-full bg-slate-200 text-sm font-semibold">
                          {(name || '?').charAt(0).toUpperCase()}
                        </span>
                      )}
                      <svg viewBox="0 0 20 20" className={`w-3.5 h-3.5 text-ink/40 transition ${menuOpen ? 'rotate-180' : ''}`} fill="currentColor" aria-hidden>
                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {menuOpen && (
                      <div
                        role="menu"
                        className="absolute right-0 top-full mt-2 w-48 rounded-xl border-2 border-ink bg-white shadow-hard py-1.5 z-30"
                      >
                        <Link
                          to="/admin/bots"
                          role="menuitem"
                          className="block px-4 py-2 text-sm font-semibold hover:bg-ink/5"
                        >
                          Bot Lab
                        </Link>
                        <p className="px-4 pb-1.5 text-xs text-slate-400">
                          Seed a game with practice players.
                        </p>
                      </div>
                    )}
                  </div>
                ) : photo ? (
                  <img src={photo} alt="" className="w-8 h-8 rounded-full ring-2 ring-slate-200" />
                ) : (
                  <span className="grid place-items-center w-8 h-8 rounded-full bg-slate-200 text-sm font-semibold">
                    {(name || '?').charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="hidden sm:block text-sm font-medium max-w-[12rem] truncate">{name}</span>
                <button onClick={handleSignOut} className="text-sm font-medium text-ink/60 hover:text-tile-red">
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <main>{children}</main>
    </div>
  );
};
